import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  CheckSquare,
  Handshake,
  History,
  Mail,
  Phone,
  StickyNote,
  ThumbsDown,
  ThumbsUp,
  UserCheck,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../../../components/Button";
import { CustomTextArea } from "../../../components/CustomTextArea";
import { CustomTextField } from "../../../components/CustomTextField";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import {
  addClientifyDealNote,
  clientifyErrorMessage,
  listClientifyDealActivity,
} from "../../../services/clientifyService";

const formatMoney = (amount, currency) => {
  const value = Number.parseFloat(amount);
  if (!Number.isFinite(value)) return null;
  return value.toLocaleString("es-ES", {
    style: "currency",
    currency: (currency || "EUR").toUpperCase(),
  });
};

const CALL_TYPES = { outgoing: "Saliente", incoming: "Entrante" };

const LOGGER_ACTIONS = {
  create_note: "Nota creada",
  update_note: "Nota editada",
  delete_note: "Nota eliminada",
};

/** Old entries carry `extra` as a JSON string, newer ones as an object. */
const parseExtra = (extra) => {
  if (!extra) return {};
  if (typeof extra === "object") return extra;
  try {
    return JSON.parse(extra);
  } catch {
    return {};
  }
};

/**
 * Title, icon and detail lines of a Clientify wall entry. Unknown types still
 * show up, with their raw type as the title.
 */
const describeEntry = (entry) => {
  const extra = parseExtra(entry.extra);
  const amount = formatMoney(extra.deal_amount, extra.deal_currency);
  const lines = [];
  const push = (label, value) => {
    if (value !== null && value !== undefined && value !== "") lines.push({ label, value });
  };

  switch (entry.type) {
    case "deal_creation":
    case "deal_modification":
      push(null, extra.deal_name);
      push("Importe", amount);
      push("Etapa", extra.deal_stage);
      return {
        title: entry.type === "deal_creation" ? "Oportunidad creada" : "Oportunidad modificada",
        Icon: Handshake,
        lines,
      };
    case "deal_stage_change":
      push(null, [extra.current_stage, extra.new_stage].filter(Boolean).join(" → "));
      return { title: "Cambio de etapa", Icon: ArrowRightLeft, lines };
    case "deal_won":
      push(null, extra.deal_name);
      push("Importe", amount);
      return { title: "Oportunidad ganada", Icon: ThumbsUp, lines, tone: "success" };
    case "deal_lost":
      push(null, extra.deal_name);
      push("Importe", amount);
      push("Motivo", extra.lost_reason);
      return { title: "Oportunidad perdida", Icon: ThumbsDown, lines, tone: "error" };
    case "deal_manual_assignment":
      push("Asignada a", entry.user2 || entry.user);
      return { title: "Oportunidad asignada", Icon: UserCheck, lines };
    case "logger":
      // Field edits carry old/new values; other audit entries (a note being
      // created...) only carry the action and the kind of object.
      if (extra.field_name) {
        push(
          extra.label_name || extra.field_name,
          `${extra.old_value || "—"} → ${extra.new_value || "—"}`
        );
      } else {
        push(null, LOGGER_ACTIONS[extra.action] || [extra.title, extra.action].filter(Boolean).join(": "));
      }
      return { title: "Registro de cambios", Icon: History, lines };
    case "note":
      push(null, extra.note_name && extra.note_name !== "Nota" ? extra.note_name : null);
      push(null, extra.note_comment);
      return { title: "Nota", Icon: StickyNote, lines };
    case "call":
      push("Tipo", CALL_TYPES[extra.call_type] || extra.call_type);
      push("Resultado", extra.call_outcome);
      push("Teléfono", extra.phone_number);
      push("Duración", extra.duration ? `${extra.duration} min` : null);
      return { title: "Llamada", Icon: Phone, lines };
    case "task_creation":
    case "task_modification":
    case "task_completed":
      push(null, extra.task_name);
      push("Tipo", extra.task_type);
      push("Estado", extra.task_status);
      return {
        title: {
          task_creation: "Tarea creada",
          task_modification: "Tarea modificada",
          task_completed: "Tarea completada",
        }[entry.type],
        Icon: CheckSquare,
        lines,
      };
    case "sent_message":
    case "gmail_sent_message":
      push(null, extra.message_subject);
      push(null, extra.message_snippet);
      push("Aperturas", extra.get_number_of_opened || null);
      return { title: "Email enviado", Icon: Mail, lines };
    default:
      return { title: entry.type.replace(/_/g, " "), Icon: History, lines };
  }
};

const dayLabel = (date) =>
  date.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

const timeLabel = (date) =>
  date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

const NoteComposer = ({ dealId, onDone }) => {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await addClientifyDealNote(dealId, { comment: comment.trim() });
      toast.success("Nota añadida");
      await queryClient.invalidateQueries({ queryKey: ["clientify-deal-activity", dealId] });
      onDone();
    } catch (error) {
      toast.error(clientifyErrorMessage(error, "No se pudo añadir la nota"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1}>
      <CustomTextArea
        rows={4}
        placeholder="Escribe una nota..."
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        autoFocus
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button variant="ghost" size="sm" onClick={onDone} disabled={saving}>
          Cancelar
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving || !comment.trim()}>
          {saving ? "Guardando..." : "Guardar nota"}
        </Button>
      </Box>
    </Stack>
  );
};

NoteComposer.propTypes = {
  dealId: PropTypes.number.isRequired,
  onDone: PropTypes.func.isRequired,
};

/** Clientify's "Todos" tab: every wall entry of the deal grouped by day. */
export const DealActivityTimeline = ({ dealId }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [composing, setComposing] = useState(false);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["clientify-deal-activity", dealId],
      queryFn: ({ pageParam }) => listClientifyDealActivity(dealId, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, pages) => (lastPage?.next ? pages.length + 1 : undefined),
      staleTime: 0,
    });

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const byDay = [];
    (data?.pages || [])
      .flatMap((page) => page?.results || [])
      .forEach((entry) => {
        const described = describeEntry(entry);
        const haystack = [described.title, entry.user, ...described.lines.map((l) => l.value)]
          .join(" ")
          .toLowerCase();
        if (term && !haystack.includes(term)) return;
        const date = new Date(entry.created);
        const day = dayLabel(date);
        let group = byDay[byDay.length - 1];
        if (!group || group.day !== day) {
          group = { day, items: [] };
          byDay.push(group);
        }
        group.items.push({ entry, date, ...described });
      });
    return byDay;
  }, [data, search]);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <CustomTextField
            isSearch
            placeholder="Buscar por nombre de actividad"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Button size="sm" onClick={() => setComposing(true)} disabled={composing}>
          Agregar nota
        </Button>
      </Box>

      {composing && <NoteComposer dealId={dealId} onDone={() => setComposing(false)} />}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <EmptyStateMessage
          message={clientifyErrorMessage(error, "No se pudo cargar la actividad")}
        />
      ) : groups.length === 0 ? (
        <EmptyStateMessage message={search ? "Sin resultados" : "Sin actividad todavía"} />
      ) : (
        <Stack spacing={1}>
          {groups.map((group) => (
            <Box key={group.day}>
              <Box
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  px: 2,
                  py: 1,
                  borderRadius: "6px",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                }}
              >
                {group.day}
              </Box>
              {group.items.map(({ entry, date, title, Icon, lines, tone }, index) => {
                const accent = tone ? theme.palette[tone].main : theme.palette.primary.main;
                const isLast = index === group.items.length - 1;
                return (
                  <Box
                    key={entry.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "48px 16px 1fr",
                      columnGap: 1,
                      pt: 2,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary", pt: "6px" }}
                    >
                      {timeLabel(date)}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: accent,
                          mt: "10px",
                        }}
                      />
                      {!isLast && (
                        <Box sx={{ flex: 1, width: "2px", backgroundColor: "divider", mt: 0.5 }} />
                      )}
                    </Box>
                    <Stack spacing={0.5} sx={{ minWidth: 0, pb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alpha(accent, 0.1),
                            color: accent,
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={15} />
                        </Box>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                          {title}
                        </Typography>
                        {entry.user && (
                          <Typography
                            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                            noWrap
                          >
                            · {entry.user}
                          </Typography>
                        )}
                      </Stack>
                      {lines.map((line, lineIndex) => (
                        <Typography
                          key={lineIndex}
                          sx={{
                            fontSize: "0.8125rem",
                            color: "text.primary",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {line.label && (
                            <Box component="span" sx={{ color: "text.secondary", mr: 0.75 }}>
                              {line.label}:
                            </Box>
                          )}
                          {line.value}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          ))}

          {hasNextPage && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Cargando..." : "Cargar más"}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
};

DealActivityTimeline.propTypes = {
  dealId: PropTypes.number.isRequired,
};
